import { ApolloError } from "@apollo/client";

import { ServerGroupMatchRuleInput } from "types/types";

import {
  ServerGroupMembersQuery,
  ServerGroupMembersQueryVariables,
  useServerGroupMembersQuery,
} from "./gql/Query.members.generated";

const BASELINE_POLL_MS = 45_000;

export type ServerGroupMember =
  ServerGroupMembersQuery["getServerGroupMembers"][number];

type Props = {
  organizationSlug: string;
  serverGroupId?: string;
  matchRules: ServerGroupMatchRuleInput[];
  memberServerIds: string[];
  memberServerGroupIds: string[];
};

type ServerGroupMembership = {
  baselineMembers: ServerGroupMember[] | undefined;
  previewMembers: ServerGroupMember[] | undefined;
  lastKnownPreviewMembers: ServerGroupMember[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  baselineVariables: ServerGroupMembersQueryVariables;
};

export const useServerGroupMembers = ({
  organizationSlug,
  serverGroupId,
  matchRules,
  memberServerIds,
  memberServerGroupIds,
}: Props): ServerGroupMembership => {
  const baselineVariables = { organizationSlug, serverGroupId };
  const baseline = useServerGroupMembersQuery({
    variables: baselineVariables,
    pollInterval: BASELINE_POLL_MS,
  });
  const candidate = useServerGroupMembersQuery({
    variables: {
      organizationSlug,
      serverGroupId,
      matchRules,
      memberServerIds,
      memberServerGroupIds,
    },
  });
  const lastKnownPreviewMembers =
    candidate.data?.getServerGroupMembers ??
    candidate.previousData?.getServerGroupMembers;

  return {
    baselineMembers: baseline.data?.getServerGroupMembers,
    previewMembers: candidate.data?.getServerGroupMembers,
    loading: baseline.loading || candidate.loading,
    error: baseline.error ?? candidate.error,
    lastKnownPreviewMembers,
    baselineVariables,
  };
};
